import { useEffect, useMemo, useState } from "react"
import { useInfiniteQuery } from "@tanstack/react-query"
import { useDebounce } from "@/hooks/useDebounce"
import { getJobs, useGetCandidatesSearch } from "openapi/api/endpoints"

const JOBS_PAGE_SIZE = 200

export type PlacementJobOption = {
  id: string
  title: string
  companyName: string
  department?: string
  location?: string
}

export type PlacementCandidateOption = {
  jobCandidateId: string
  candidateId: string
  name: string
  stage?: string
}

export type PlacementContextSelection = {
  jobId: string
  jobTitle: string
  companyName: string
  candidateId: string
  candidateName: string
  jobCandidateId: string
  isComplete: boolean
}

/**
 * Owns the job-first placement context picker for placement creation from the
 * placements table. The backend requires a job-candidate row id, so this hook
 * first narrows jobs locally, then queries candidates scoped to the selected
 * job and exposes the exact ids the shared PlacementDialog should submit.
 */
export function usePlacementContextSelection({ isOpen }: { isOpen: boolean }) {
  const [jobSearch, setJobSearch] = useState("")
  const [candidateSearch, setCandidateSearch] = useState("")
  const [selectedJobId, setSelectedJobIdState] = useState("")
  const [selectedCandidate, setSelectedCandidate] = useState<PlacementCandidateOption | null>(null)

  const debouncedCandidateSearch = useDebounce(candidateSearch.trim(), 300)

  const {
    data: jobPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingJobPages,
    isError: hasJobsError,
    refetch: refetchJobs,
  } = useInfiniteQuery({
    queryKey: ["placement-context-jobs", JOBS_PAGE_SIZE],
    queryFn: ({ pageParam, signal }) =>
      getJobs({ limit: JOBS_PAGE_SIZE, offset: Number(pageParam) }, undefined, signal),
    initialPageParam: 0,
    enabled: isOpen,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === JOBS_PAGE_SIZE ? allPages.length * JOBS_PAGE_SIZE : undefined,
  })

  useEffect(() => {
    if (!isOpen || hasJobsError || !hasNextPage || isFetchingNextPage) return

    void fetchNextPage()
  }, [fetchNextPage, hasJobsError, hasNextPage, isFetchingNextPage, isOpen])

  useEffect(() => {
    if (isOpen) return

    setJobSearch("")
    setCandidateSearch("")
    setSelectedJobIdState("")
    setSelectedCandidate(null)
  }, [isOpen])

  const allJobOptions = useMemo(() => {
    const jobs = jobPages?.pages.flat() ?? []

    return jobs.map((job) => ({
      id: job.id,
      title: job.title,
      companyName: job.company_name ?? "",
      department: job.department,
      location: job.location,
    }))
  }, [jobPages?.pages])

  const jobOptions = useMemo(() => {
    const query = jobSearch.trim().toLowerCase()

    return allJobOptions
      .filter((job) => {
        if (!query) return true

        return [job.title, job.companyName, job.department, job.location]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query)
      })
      .slice(0, 20)
  }, [allJobOptions, jobSearch])

  const selectedJob = allJobOptions.find((job) => job.id === selectedJobId) ?? null

  const {
    data: candidateRows = [],
    isLoading: isLoadingCandidates,
    isError: hasCandidatesError,
    refetch: refetchCandidates,
  } = useGetCandidatesSearch(
    {
      job_id: selectedJobId,
      name: debouncedCandidateSearch || undefined,
      limit: 50,
    },
    { query: { enabled: isOpen && !!selectedJobId } },
  )

  const candidateOptions = useMemo(() => {
    const options: PlacementCandidateOption[] = []

    for (const row of candidateRows) {
      if (!row.id || !row.candidates?.id) continue

      options.push({
        jobCandidateId: row.id,
        candidateId: row.candidates.id,
        name: row.candidates.full_name || "Unknown candidate",
        ...(row.stage ? { stage: String(row.stage) } : {}),
      })
    }

    return options
  }, [candidateRows])

  const setSelectedJobId = (jobId: string) => {
    setSelectedJobIdState(jobId)
    setSelectedCandidate(null)
    setCandidateSearch("")
  }

  const setSelectedJobCandidateId = (jobCandidateId: string) => {
    setSelectedCandidate(
      candidateOptions.find((candidate) => candidate.jobCandidateId === jobCandidateId) ?? null,
    )
  }

  const selectedJobCandidateId = selectedCandidate?.jobCandidateId ?? ""
  const retryJobs = () => {
    void refetchJobs()
  }
  const retryCandidates = () => {
    void refetchCandidates()
  }

  const selection: PlacementContextSelection = {
    jobId: selectedJob?.id ?? "",
    jobTitle: selectedJob?.title ?? "",
    companyName: selectedJob?.companyName ?? "",
    candidateId: selectedCandidate?.candidateId ?? "",
    candidateName: selectedCandidate?.name ?? "",
    jobCandidateId: selectedCandidate?.jobCandidateId ?? "",
    isComplete: Boolean(selectedJob && selectedCandidate),
  }

  return {
    jobSearch,
    setJobSearch,
    candidateSearch,
    setCandidateSearch,
    selectedJobId,
    setSelectedJobId,
    selectedJobCandidateId,
    setSelectedJobCandidateId,
    jobOptions,
    candidateOptions,
    isLoadingJobs: isLoadingJobPages || isFetchingNextPage,
    isLoadingCandidates,
    hasJobsError,
    hasCandidatesError,
    retryJobs,
    retryCandidates,
    selection,
  }
}

export type PlacementContextSelectorState = ReturnType<typeof usePlacementContextSelection>
